import { styled } from "@mui/material/styles";
import { Box, Typography, Card } from "@mui/material";
import { colors, fonts } from "../../../theme/theme";

export const RootBox = styled(Box)(({ theme }) => ({
  maxWidth: 1200,
  margin: "0 auto",
  padding: theme.spacing(3),
  [theme.breakpoints.down("sm")]: { padding: theme.spacing(1) },
}));

export const HeaderRow = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "flex-start",
  justifyContent: "space-between",
  gap: theme.spacing(2),
  marginBottom: theme.spacing(3),
  flexWrap: "wrap",
}));

export const PageHeading = styled(Typography)({
  fontFamily: fonts.gotham,
  fontWeight: 600,
  fontSize: 28,
  color: colors.primary,
});

export const PageSubheading = styled(Typography)({
  fontFamily: fonts.quicksand,
  fontSize: 15,
  color: colors.textLight,
  marginTop: 4,
});

export const StatsRow = styled(Box)(({ theme }) => ({
  display: "grid",
  gridTemplateColumns: "repeat(4, 1fr)",
  gap: theme.spacing(2),
  marginBottom: theme.spacing(3),
  [theme.breakpoints.down("sm")]: { gridTemplateColumns: "repeat(2, 1fr)" },
}));

export const StatCard = styled(Card, {
  shouldForwardProp: (p) => p !== "$active",
})(({ theme, $active }) => ({
  padding: theme.spacing(2.5),
  borderRadius: 16,
  cursor: "pointer",
  boxShadow: "0 1px 3px rgba(0,0,0,0.06)",
  border: `2px solid ${$active ? colors.secondary : "transparent"}`,
  transition: "border-color 0.15s ease",
}));

export const StatValue = styled(Typography, {
  shouldForwardProp: (p) => p !== "$color",
})(({ $color }) => ({
  fontFamily: fonts.gotham,
  fontWeight: 700,
  fontSize: 26,
  color: $color || colors.primary,
}));

export const StatLabel = styled(Typography)({
  fontFamily: fonts.quicksand,
  fontSize: 14,
  color: colors.textLight,
  marginTop: 2,
});

export const ToolbarCard = styled(Card)(({ theme }) => ({
  display: "flex",
  gap: theme.spacing(2),
  padding: theme.spacing(1.5),
  borderRadius: 16,
  marginBottom: theme.spacing(3),
  boxShadow: "0 1px 3px rgba(0,0,0,0.06)",
  flexWrap: "wrap",
  alignItems: "center",
}));

export const TableCard = styled(Card)(({ theme }) => ({
  borderRadius: 16,
  padding: theme.spacing(1, 3),
  boxShadow: "0 1px 3px rgba(0,0,0,0.06)",
  overflowX: "auto",
}));

// Scroll lives on the table body so the page itself never grows unbounded.
export const ScrollBody = styled(Box)({
  maxHeight: "calc(100vh - 430px)",
  minHeight: 200,
  overflowY: "auto",
  minWidth: 760, // match RowGrid so horizontal scroll stays on TableCard, not here
});

export const HeaderCell = styled(Typography)({
  fontFamily: fonts.quicksand,
  fontSize: 12,
  fontWeight: 600,
  letterSpacing: "0.6px",
  textTransform: "uppercase",
  color: colors.textLight,
});

export const RowGrid = styled(Box)(({ theme }) => ({
  display: "grid",
  gridTemplateColumns: "2.2fr 1.6fr 1.4fr 1fr 1fr 48px",
  alignItems: "center",
  gap: theme.spacing(1),
  padding: theme.spacing(2, 0),
  borderBottom: `1px solid ${colors.headerBg}`,
  "&:last-of-type": { borderBottom: "none" },
  minWidth: 760,
}));

export const PractitionerCell = styled(Box)({
  display: "flex",
  alignItems: "center",
  gap: 12,
  minWidth: 0,
});

export const NameLine = styled(Box)({
  display: "flex",
  alignItems: "center",
  gap: 8,
  minWidth: 0,
});

export const Name = styled(Typography)({
  fontFamily: fonts.quicksand,
  fontWeight: 600,
  fontSize: 15,
  color: colors.primary,
  whiteSpace: "nowrap",
  overflow: "hidden",
  textOverflow: "ellipsis",
});

export const SubText = styled(Typography)({
  fontFamily: fonts.quicksand,
  fontSize: 13,
  color: colors.textLight,
  whiteSpace: "nowrap",
  overflow: "hidden",
  textOverflow: "ellipsis",
});

export const CellText = styled(Typography)({
  fontFamily: fonts.quicksand,
  fontSize: 14,
  color: colors.text,
});

// Small "changes pending review" pill shown next to a practitioner's name.
export const PendingEditBadge = styled(Box)({
  display: "inline-flex",
  alignItems: "center",
  gap: 4,
  fontFamily: fonts.quicksand,
  fontSize: 11,
  fontWeight: 600,
  color: "#C77A22",
  backgroundColor: "#FDF2E2",
  borderRadius: 12,
  padding: "2px 8px",
  whiteSpace: "nowrap",
});

export const LicensedBadge = styled(Box)({
  fontFamily: fonts.quicksand,
  fontSize: 11,
  fontWeight: 600,
  color: colors.secondary,
  backgroundColor: colors.headerBg,
  borderRadius: 12,
  padding: "2px 8px",
  whiteSpace: "nowrap",
});

const STATUS_COLORS = {
  approved: { bg: "#E7F4E8", fg: "#409d45" },
  pending: { bg: "#FDF2E2", fg: "#C77A22" },
  rejected: { bg: "#FBE7E7", fg: "#C0392B" },
  // Archived accounts: neutral grey — not an error state, just off the roster.
  archived: { bg: "#ECEFF1", fg: "#5B6B79" },
};
export const StatusPill = styled(Box, {
  shouldForwardProp: (p) => p !== "$status",
})(({ $status }) => {
  const c = STATUS_COLORS[$status] || STATUS_COLORS.pending;
  return {
    display: "inline-flex",
    alignItems: "center",
    gap: 5,
    padding: "4px 12px",
    borderRadius: 20,
    fontFamily: fonts.quicksand,
    fontSize: 13,
    fontWeight: 600,
    backgroundColor: c.bg,
    color: c.fg,
    textTransform: "capitalize",
    width: "fit-content",
  };
});

export const LoadingBox = styled(Box)({
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  minHeight: "40vh",
});

export const EmptyBox = styled(Box)(({ theme }) => ({
  textAlign: "center",
  padding: theme.spacing(6, 2),
  fontFamily: fonts.quicksand,
  color: colors.textLight,
}));
